import React, { useState, useEffect } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import NavigationBreadcrumbs from '../../components/navigation/NavigationBreadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SystemHealthCard from './components/SystemHealthCard';
import UserManagementTable from './components/UserManagementTable';
import FraudAlertCard from './components/FraudAlertCard';
import APIUsageChart from './components/APIUsageChart';
import ComplianceReportCard from './components/ComplianceReportCard';
import SubscriptionMetricsCard from './components/SubscriptionMetricsCard';
import AuditLogViewer from './components/AuditLogViewer';
import MLModelMonitor from './components/MLModelMonitor';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/financialDataService';
import { supabase } from '../../lib/supabase';

const SuperAdministratorDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const [systemHealthData, setSystemHealthData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  const handleUserAction = (action, userId) => {
    console.log(`${action} action for user ${userId}`);
  };

  const handleInvestigate = (alertId) => {
    console.log(`Investigating alert ${alertId}`);
  };

  const handleDismiss = (alertId) => {
    console.log(`Dismissing alert ${alertId}`);
  };

  const handleDownloadReport = (reportId) => {
    console.log(`Downloading report ${reportId}`);
  };

  const handleViewReport = (reportId) => {
    console.log(`Viewing report ${reportId}`);
  };

  const tabs = [
  { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
  { id: 'users', label: 'User Management', icon: 'Users' },
  { id: 'security', label: 'Security & Fraud', icon: 'Shield' },
  { id: 'compliance', label: 'Compliance', icon: 'FileCheck' },
  { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
  { id: 'ml-models', label: 'ML Models', icon: 'Brain' }];


  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch system health metrics
        const healthData = await adminService?.getSystemHealth();
        const transformedHealth = healthData?.map(metric => ({
          title: metric?.metricName,
          value: metric?.metricValue,
          status: metric?.status,
          trend: parseFloat(metric?.trend || 0),
          icon: metric?.metricName?.includes('Uptime') ? 'Activity' :
                metric?.metricName?.includes('Response') ? 'Zap' :
                metric?.metricName?.includes('Database') ? 'Database' :
                metric?.metricName?.includes('Users') ? 'Users' :
                metric?.metricName?.includes('Error') ? 'AlertTriangle' : 'HardDrive',
          description: metric?.description
        }));
        setSystemHealthData(transformedHealth);

        // Fetch all users
        const users = await adminService?.getAllUsers();
        const transformedUsers = users?.map(u => ({
          id: u?.id,
          name: u?.fullName || 'Unknown User',
          email: u?.email,
          avatar: u?.avatarUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_18811c304-1763296452128.png',
          avatarAlt: `Profile picture of ${u?.fullName || 'user'}`,
          role: u?.role,
          roleLabel: u?.role === 'admin' ? 'Administrator' : u?.role === 'premium' ? 'Premium User' : 'Free User',
          status: 'active',
          lastActive: 'Recently'
        }));
        setUsersData(transformedUsers);

        // Fetch fraud alerts
        const alerts = await adminService?.getFraudAlerts();
        const transformedAlerts = alerts?.map(alert => ({
          id: alert?.id,
          type: alert?.alertType,
          severity: alert?.severity,
          description: alert?.description,
          status: alert?.status,
          detectedAt: new Date(alert.detectedAt),
          userId: alert?.userId
        }));
        setFraudAlerts(transformedAlerts);

        // Fetch audit logs
        const logs = await adminService?.getAuditLogs(100);
        const transformedLogs = logs?.map(log => ({
          id: log?.id,
          action: log?.action,
          resource: log?.resource,
          details: log?.details,
          ipAddress: log?.ipAddress,
          userAgent: log?.userAgent,
          createdAt: new Date(log.createdAt),
          userId: log?.userId
        }));
        setAuditLogs(transformedLogs);
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Real-time subscriptions
    const systemHealthChannel = supabase?.channel('system_health_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_health'
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    const fraudAlertsChannel = supabase?.channel('fraud_alerts_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'fraud_alerts'
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    const auditLogsChannel = supabase?.channel('audit_logs_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'audit_logs'
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    const usersChannel = supabase?.channel('users_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles'
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(systemHealthChannel);
      supabase?.removeChannel(fraudAlertsChannel);
      supabase?.removeChannel(auditLogsChannel);
      supabase?.removeChannel(usersChannel);
    };
  }, [user?.id]);

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <main className="pt-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <NavigationBreadcrumbs />

          <div className="mb-6 md:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                  Super Administrator Dashboard
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Comprehensive platform management and system oversight
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" iconName="Download" iconPosition="left">
                  Export Report
                </Button>
                <Button variant="default" iconName="Settings" iconPosition="left">
                  System Settings
                </Button>
              </div>
            </div>

            <div className="flex overflow-x-auto gap-2 pb-2 border-b border-border">
              {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-smooth ${
                activeTab === tab?.id ?
                'bg-primary text-primary-foreground' :
                'text-muted-foreground hover:bg-muted hover:text-foreground'}`
                }>
                
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              )}
            </div>
          </div>

          {activeTab === 'overview' &&
          <div className="space-y-6 md:space-y-8">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">System Health</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {systemHealthData?.map((metric, index) =>
                <SystemHealthCard key={index} {...metric} />
                )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                {systemHealthData?.map((metric, index) =>
              <SubscriptionMetricsCard key={index} {...metric} />
              )}
              </div>

              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">Recent Fraud Alerts</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  {fraudAlerts?.slice(0, 2)?.map((alert) =>
                <FraudAlertCard
                  key={alert?.id}
                  alert={alert}
                  onInvestigate={handleInvestigate}
                  onDismiss={handleDismiss} />

                )}
                </div>
              </div>
            </div>
          }

          {activeTab === 'users' &&
          <div className="space-y-6 md:space-y-8">
              <UserManagementTable users={usersData} onUserAction={handleUserAction} />
            </div>
          }

          {activeTab === 'security' &&
          <div className="space-y-6 md:space-y-8">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">Active Fraud Alerts</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  {fraudAlerts?.map((alert) =>
                <FraudAlertCard
                  key={alert?.id}
                  alert={alert}
                  onInvestigate={handleInvestigate}
                  onDismiss={handleDismiss} />

                )}
                </div>
              </div>

              <AuditLogViewer logs={auditLogs} />
            </div>
          }

          {activeTab === 'compliance' &&
          <div className="space-y-6 md:space-y-8">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">Compliance Reports</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                  {systemHealthData?.map((report) =>
                <ComplianceReportCard
                  key={report?.id}
                  report={report}
                  onDownload={handleDownloadReport}
                  onView={handleViewReport} />

                )}
                </div>
              </div>

              <AuditLogViewer logs={auditLogs} />
            </div>
          }

          {activeTab === 'analytics' &&
          <div className="space-y-6 md:space-y-8">
              <APIUsageChart data={systemHealthData} />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                {systemHealthData?.map((metric, index) =>
              <SubscriptionMetricsCard key={index} {...metric} />
              )}
              </div>

              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">System Performance</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {systemHealthData?.map((metric, index) =>
                <SystemHealthCard key={index} {...metric} />
                )}
                </div>
              </div>
            </div>
          }

          {activeTab === 'ml-models' &&
          <div className="space-y-6 md:space-y-8">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">Machine Learning Models</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                  {systemHealthData?.map((model, index) =>
                <MLModelMonitor key={index} model={model} />
                )}
                </div>
              </div>
            </div>
          }
        </div>
      </main>
    </div>);

};

export default SuperAdministratorDashboard;